import dayjs from "dayjs"
// import getXorDifference from "lodash.xor"
import { useEffect, useRef } from "react"
import { batch, shallowEqual, useDispatch } from "react-redux"
import { toast } from "react-toastify"
import ReconnectingWebSocket from "reconnecting-websocket"

import { globalStore, useAppSelector } from "@/App"
import { selectCurrentTeamKey } from "@/store/teams"
import { selectIsAuthenticated } from "@/store/user"
import {
  retrieveSavedToken,
  // usePrevious,
} from "@/utils"

import { Channels } from "./channels"
import {
  getWebSocketURI,
  requestDataRefresh,
  // subscribe,
  // unsubscribe,
} from "./utils"

const MS_TO_FORCED_RECONNECT = 15000

const MultiChannels = ["NotificationChannel", "TeamChannel"] as const

export function useSockets() {
  const socket = useRef<ReconnectingWebSocket>()
  const heartbeat = useRef<number>()
  const isInitialOpen = useRef(true)
  const hasLostConnection = useRef(false)

  const currentSite = useAppSelector((state) => state.global.currentSite)
  const isAuthenticated = useAppSelector(selectIsAuthenticated)

  const teamUuid = useAppSelector(selectCurrentTeamKey("uuid"))
  // const previousTeamUuid = usePrevious(teamUuid)

  const multiTeams = useAppSelector((state) => state.teams.currentMulti, shallowEqual)
  // const previousMultiTeams = usePrevious(multiTeams)

  const dispatch = useDispatch()

  useEffect(() => {
    if (!isAuthenticated || !teamUuid || !currentSite) return

    socket.current = new ReconnectingWebSocket(getWebSocketURI(currentSite))

    socket.current.onopen = () => {
      // Current selected team
      Object.values(Channels).forEach((Channel) => socket.current?.send(Channel.subscribe(teamUuid)))

      // Selected multi teams
      Object.values(multiTeams.filter((multiUuid) => multiUuid.uuid !== teamUuid)).forEach((team) =>
        MultiChannels.forEach((channel) => socket.current?.send(Channels[channel].subscribe(team.uuid)))
      )

      if (!isInitialOpen.current && hasLostConnection.current) {
        // Wait 1s to ensure connection is fully restored before requests
        // Tests have shown that requests can be made too early
        window.setTimeout(() => dispatch(requestDataRefresh()), 1000)
        toast.dismiss("offline")
        toast.success("Odzyskano połączenie z serwerem", {
          toastId: "reconnect",
          position: "bottom-right",
        })
        hasLostConnection.current = false
      } else {
        isInitialOpen.current = false
        hasLostConnection.current = false
      }
    }

    socket.current.onerror = (err) => {
      console.error("WebSocket error observed:", err)
    }

    socket.current.onclose = async (e) => {
      console.debug(e)

      // If token is null WS was closed because of logout action, so
      // don't show "Connection to server restored" toast or refresh data
      const token = await retrieveSavedToken()
      if (!token) isInitialOpen.current = true
    }

    // TODO: better typing for sockets
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let updatesQueue: Record<string, any[]> = {}

    const updateIntervalId = window.setInterval(() => {
      batch(() => {
        Object.entries(updatesQueue).forEach(([channel, updates]) => Channels[channel].handleUpdates(dispatch, updates))
      })

      updatesQueue = {}
    }, 16.4 * 60)

    socket.current.onmessage = (msg) => {
      const msgData = JSON.parse(msg.data)

      if (msgData.type === "ping") {
        heartbeat.current && window.clearTimeout(heartbeat.current)

        heartbeat.current = window.setTimeout(() => {
          socket.current?.reconnect()

          toast.error("Utracono połączenie z serwerem", {
            toastId: "offline",
            autoClose: false,
            position: "bottom-right",
          })

          hasLostConnection.current = true
        }, MS_TO_FORCED_RECONNECT)

        return
      }

      if (msgData.type !== "ping" && "message" in msgData) {
        const channel = JSON.parse(msgData.identifier).channel
        const message = msgData.message

        const date = globalStore.getState().global.date
        if ((channel === "Teams::DriversChannel" || channel === "OrderChannel") && !dayjs(date).isToday()) return

        if (updatesQueue[channel]) {
          updatesQueue[channel].push(message)
        } else {
          updatesQueue[channel] = [message]
        }
      }
    }

    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      heartbeat.current && window.clearTimeout(heartbeat.current)
      socket.current?.close()
      window.clearInterval(updateIntervalId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    isAuthenticated,
    // Boolean(teamUuid),
    // Boolean(multiTeams),
    teamUuid,
    multiTeams,
  ])

  // useEffect(() => {
  //   if (!previousTeamUuid || !teamUuid) return

  //   Object.keys(Channels).forEach((channel) => socket.current?.send(unsubscribe(channel, previousTeamUuid)))
  //   Object.keys(Channels).forEach((channel) => socket.current?.send(subscribe(channel, teamUuid)))
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [teamUuid])

  // useEffect(() => {
  //   if (!previousMultiTeams) return

  //   const command = multiTeams.length > previousMultiTeams.length ? "subscribe" : "unsubscribe"

  //   const difference = getXorDifference(multiTeams, previousMultiTeams)

  //   if (!difference.length) return

  //   if (command === "subscribe") {
  //     difference.forEach((team) => {
  //       MultiChannels.forEach((channel) => socket.current?.send(subscribe(channel, team.uuid)))
  //     })
  //   } else if (command === "unsubscribe") {
  //     difference.forEach((team) => {
  //       MultiChannels.forEach((channel) => socket.current?.send(unsubscribe(channel, team.uuid)))
  //     })
  //   }

  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [multiTeams.length])

  return socket
}
